/**
 * Shared view vocabulary: the palette from the design, small element helpers,
 * the uncontrolled-input registry, and the code that turns structured alerts
 * from the metric engine into sentences.
 */

import { h } from '../vdom.js';
import { numberText,
  PALETTE, kmToMiles, milesToKm, litresToGallons, gallonsToLitres, normaliseVin,
  SUGGESTED_CURRENCIES, parseNumber, ASSET_CLASSES, isAmountOnly,
  CATEGORIES, CAR_EXPENSE_TYPES, TRADE_SIDES, TRADE_EXITS, TRADE_RISKS, isMonth, isDate,
  CADENCES, nextChargeMonth, currentMonth, debtBalanceAt, debtLatestBalance, shiftMonth
} from '../../../shared/model.js';
import { strings } from '../i18n.js';
import { monthStart } from '../../../shared/plans.js';

export const C = PALETTE;

export const MONO = "'JetBrains Mono', monospace";
export const SANS = "'Archivo', system-ui, sans-serif";

/** Turns a style object into the inline string the design uses throughout. */
export function css(parts) {
  return Object.entries(parts)
    .filter(([, value]) => value !== null && value !== undefined && value !== false)
    .map(([key, value]) => `${key.replace(/[A-Z]/g, m => '-' + m.toLowerCase())}:${value}`)
    .join(';');
}

// -------------------------------------------------------- input registry ----

/**
 * Form fields stay uncontrolled — the DOM owns their value, exactly as the
 * prototype did. Views register them by name and read them when a button fires.
 */
export function createInputs() {
  const nodes = new Map();

  return {
    ref: name => el => {
      if (el) nodes.set(name, el);
      else nodes.delete(name);
    },
    value: name => {
      const el = nodes.get(name);
      return el ? el.value : '';
    },
    /** The element itself, for the rare field that another field has to write. */
    node: name => nodes.get(name) || null,
    /*
     * Zero for an empty or unreadable field, because every caller treats a
     * blank as "not given". A comma is a decimal point here -- see parseNumber.
     */
    number(name) {
      const parsed = parseNumber(this.value(name));
      return parsed === null ? 0 : parsed;
    },
    clear(names) {
      names.forEach(name => {
        const el = nodes.get(name);
        if (el) el.value = '';
      });
    },
    focus(name) {
      const el = nodes.get(name);
      if (el) el.focus();
    }
  };
}

/**
 * The design builds every control out of plain divs. Spreading this into their
 * props keeps that markup while making them reachable by keyboard and legible
 * to a screen reader — Enter and Space activate, as they would on a button.
 */
export function tap(onClick, label) {
  return {
    role: 'button',
    tabindex: '0',
    // Callers that set their own class (pt-button, pt-nav, pt-remove) override
    // this one; the stylesheet lists all four together.
    class: 'pt-tap',
    'aria-label': label || null,
    onclick: onClick,
    onkeydown: event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick(event);
      }
    }
  };
}

// ---------------------------------------------------------------- pieces ----

export const card = (style, ...children) => h('div', {
  style: css({
    background: C.card,
    border: `1px solid ${C.line}`,
    borderRadius: '10px',
    ...style
  })
}, ...children);

/**
 * Whether the account wears the Liquidity+ marks: it has Liquidity+ now, by
 * subscription, trial, the launch gift or a grant. Not a demo -- that shows the
 * features, not a membership.
 */
export const isPremium = state => Boolean(
  state.user && !state.user.isGuest && state.user.plan?.plan === 'plus' && state.user.plan.source !== 'demo'
);

/**
 * The shield, and for Liquidity+ its gold "+" seal (design 1a): the same mark
 * everybody knows, with one small sign of membership in the corner. `ring` is
 * the colour behind it, which the seal's outline cuts out of the shield.
 */
export function logoMark(size, { premium = false, ring = C.panel } = {}) {
  const seal = Math.round(size * 0.42);
  return h('div', {
    style: css({ position: 'relative', width: size + 'px', height: size + 'px', flex: `0 0 ${size}px` })
  },
    h('img', {
      src: 'logo-mark.png', alt: '',
      style: css({ width: size + 'px', height: size + 'px', objectFit: 'contain', display: 'block' })
    }),
    premium && h('div', {
      key: 'seal',
      'aria-label': 'Liquidity+',
      role: 'img',
      style: css({
        position: 'absolute', right: '-2px', bottom: '0', width: seal + 'px', height: seal + 'px',
        borderRadius: '50%', background: C.gold, boxShadow: `0 0 0 2px ${ring}`
      })
    },
      /*
       * Drawn, not typed. A "+" glyph sits where its font's metrics put it --
       * a pixel or two off centre in the monospace face, which at this size is
       * a visible tilt. Two bars centred on the disc are centred exactly.
       */
      sealBar(seal, false),
      sealBar(seal, true)
    )
  );
}

function sealBar(seal, vertical) {
  const long = Math.max(5, Math.round(seal * 0.56));
  const thick = Math.max(1.5, Math.round(seal * 0.14 * 2) / 2);
  return h('div', {
    key: vertical ? 'v' : 'h',
    style: css({
      position: 'absolute', left: '50%', top: '50%',
      width: (vertical ? thick : long) + 'px', height: (vertical ? long : thick) + 'px',
      transform: 'translate(-50%, -50%)', borderRadius: '1px', background: C.bg
    })
  });
}

export const kicker = (text, extra = {}) => h('div', {
  style: css({
    fontFamily: MONO, fontSize: '9px', letterSpacing: '1.3px', color: C.faint, ...extra
  })
}, text);

export const bar = (ratio, color, { height = '8px', track = C.line } = {}) => h('div', {
  style: css({ height, borderRadius: '4px', background: track, overflow: 'hidden' })
}, h('div', {
  style: css({
    height: '100%',
    borderRadius: '4px',
    background: color,
    width: Math.round(Math.max(0, Math.min(1, ratio)) * 100) + '%'
  })
}));

/*
 * Money and quantity fields are `text` with a decimal keypad, never `number`.
 *
 * `<input type="number">` decides what a comma means from the BROWSER's locale,
 * not the app's. On a Polish Chrome "1,3405" normalises to 1.3405; on an
 * English one the same keystrokes make the field invalid and `el.value` reads
 * back as the empty string -- so a Polish user on an English browser types a
 * cost, sees it on screen, and the app stores a zero. Nothing warns, on either
 * side. `type="text"` hands the characters over untouched and `parseNumber`
 * decides what they mean, the same way in every browser; `inputmode="decimal"`
 * keeps the numeric keypad on a phone, which is the only part of `number` worth
 * having here.
 */
export const input = (inputs, name, placeholder, options = {}) => h('input', {
  ref: inputs.ref(name),
  type: options.type === 'number' ? 'text' : (options.type || 'text'),
  inputmode: options.type === 'number' ? 'decimal' : null,
  autocomplete: options.type === 'number' ? 'off' : null,
  placeholder,
  /*
   * A box with no <label> is named by its placeholder, and "0,00" is no name:
   * a screen reader said "zero comma zero zero, edit text". Forms that show a
   * figure as the hint pass the field's real name here.
   */
  'aria-label': options.ariaLabel || null,
  /*
   * Seeds the field once and then leaves it alone -- the renderer only applies
   * `value` when it creates the element, so a stored figure can be shown
   * without the field fighting the person typing into it.
   */
  value: options.value,
  style: css({
    background: C.panel,
    border: `1px solid ${C.stroke}`,
    borderRadius: options.radius || '6px',
    padding: options.padding || '8px 10px',
    color: C.text,
    fontSize: '12px',
    outline: 'none',
    minWidth: 0,
    ...options.style
  }),
  onkeydown: options.onEnter
    ? event => { if (event.key === 'Enter') options.onEnter(); }
    : null,
  /*
   * For fields that have to keep another field honest -- the per-unit cost and
   * the total beside it. The handler writes to the sibling node directly rather
   * than through a render, so the field being typed into is never rebuilt.
   */
  oninput: options.onInput || null,
  /* Fires when the field is left with a new value: how Preferences save without a button. */
  onchange: options.onChange || null,
  disabled: options.disabled || null
});

/**
 * @param value     optional; which option is chosen when the node is BUILT.
 *
 *                  A select with no `value` shows its first option whenever the
 *                  renderer has to build a fresh node, whatever the app thinks
 *                  is selected -- and the tab transition builds fresh nodes by
 *                  design, because the incoming tab is keyed. That desync was
 *                  silent and it disabled the add-holding button outright:
 *                  picking Cash and then visiting another tab left the cash
 *                  fields on screen with the select back on Equity / ETF, so
 *                  submitting took the equity branch, looked for a quantity
 *                  field that was not on the form, read zero, and returned
 *                  without adding anything or saying why.
 *
 *                  Seeding only on build, never on patch, is the same contract
 *                  the text inputs have: the DOM owns the value from then on,
 *                  so this cannot fight somebody mid-choice.
 * @param onChange  optional; a form that has to adapt to the choice needs to
 *                  know it was made. Uncontrolled otherwise, like every other
 *                  field here — the DOM keeps the value.
 */
export const select = (inputs, name, options, style = {}, { onChange, value, ariaLabel } = {}) => h(
  'select',
  {
    ref: inputs.ref(name),
    value,
    /* A picker with no <label> around it needs a name of its own, or it is read as "combo box". */
    'aria-label': ariaLabel || null,
    onchange: onChange ? event => onChange(event.target.value) : null,
    style: css({
      background: C.panel,
      border: `1px solid ${C.stroke}`,
      borderRadius: '6px',
      padding: '8px 10px',
      color: C.text,
      fontSize: '12px',
      outline: 'none',
      minWidth: 0,
      ...style
    })
  },
  ...options.map(option => h('option', { key: option.value, value: option.value }, option.label))
);

export const button = (label, onClick, { tone = 'accent', style = {} } = {}) => h('div', {
  ...tap(onClick),
  class: 'pt-button',
  style: css({
    padding: '8px 14px',
    borderRadius: '6px',
    background: tone === 'accent' ? C.accent : C.stroke,
    color: tone === 'accent' ? C.bg : C.text,
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '.8px',
    cursor: 'pointer',
    textAlign: 'center',
    userSelect: 'none',
    ...style
  })
}, label);

/*
 * The name a screen reader gives every × button, in the reader's language. It
 * was fixed at "Remove", so the Polish app announced sixteen controls in
 * English. Set on every render (main.js); a default parameter is evaluated per
 * call, so each button picks up the current one.
 */
let removeLabel = 'Remove';
export const setRemoveLabel = label => { removeLabel = label || 'Remove'; };

export const removeButton = (onClick, style = {}, label = removeLabel) => h('div', {
  ...tap(onClick, label),
  class: 'pt-remove',
  style: css({
    fontFamily: MONO,
    fontSize: '12px',
    color: C.ghost,
    cursor: 'pointer',
    padding: '0 2px',
    ...style
  })
}, '×');

/**
 * Placeholder for a panel that has a heading but nothing to list yet. A fresh
 * account is mostly these, and a bare heading over blank space reads as a
 * loading failure rather than as "you have not added anything".
 */
/**
 * A month picker in the app's own language.
 *
 * Replaces `<input type="month">`, which behaved correctly and looked like a
 * system dialog dropped into a designed interface — a different typeface, a
 * different palette and a different idea of what a control is. Twelve tiles and
 * a year stepper is the whole of it.
 *
 * @param id     unique per picker, so only one is open at a time
 * @param value  'YYYY-MM' or null
 * @param onPick called with the chosen 'YYYY-MM'
 * @param title  what is being picked — a grid of months says when, never what
 * @param note   one line on what the choice changes
 */
export function monthPicker({
  id, value, onPick, label, title, note, state, actions, t, fmt,
  mobile = false, style = {},
  /* For a picker that stands in a form as a field, rather than a note on a row. */
  triggerStyle = {},
  /*
   * Which layer the overlay opens on.
   *
   * An edit dialog is itself a fixed overlay at 70, so a picker opened from
   * inside one has to sit above it or it opens behind the form that asked for
   * it -- present, clickable in neither direction, and invisible.
   */
  layer = 55
}) {
  const open = state.monthPicker === id;
  const year = state.monthPickerYear || Number(String(value || '').slice(0, 4)) || 0;

  const tile = (index) => {
    const stamp = year + '-' + String(index + 1).padStart(2, '0');
    const on = stamp === value;
    return h('div', {
      key: stamp,
      ...tap(() => {
        onPick(stamp);
        actions.closeMonthPicker();
      }, t.months[index]),
      style: css({
        padding: '7px 0', borderRadius: '7px', textAlign: 'center', cursor: 'pointer',
        border: '1px solid ' + (on ? C.accent : C.stroke),
        background: on ? C.line : 'transparent',
        color: on ? C.accent : C.muted,
        fontFamily: MONO, fontSize: '10px', letterSpacing: '.4px'
      })
    }, t.months[index].toUpperCase());
  };

  const step = (by, glyph) => h('div', {
    ...tap(() => actions.stepMonthPickerYear(by), String(by)),
    style: css({
      width: '24px', height: '24px', display: 'flex', alignItems: 'center',
      justifyContent: 'center', borderRadius: '7px', cursor: 'pointer',
      border: '1px solid ' + C.stroke, color: C.muted, fontFamily: MONO, fontSize: '11px'
    })
  }, glyph);

  return h('div', { style: css({ position: 'relative', ...style }) },
    h('div', {
      ...tap(() => (open ? actions.closeMonthPicker() : actions.openMonthPicker(id, value)), label),
      style: css({
        fontFamily: MONO, fontSize: '9.5px', cursor: 'pointer',
        color: open ? C.accent : C.faint, whiteSpace: 'nowrap', ...triggerStyle
      })
      // Upper case like every other mono label in the chrome; the formatter
      // returns sentence case for prose, which is not what this is.
    }, value ? fmt.monthName(value).toUpperCase() : '—'),

    /*
     * Anchored under the trigger on a desktop, and a centred overlay on a
     * phone: the holdings panel clips its contents to keep the row borders
     * inside its rounded corners, so an absolutely positioned card opening
     * inside it loses three of its four rows.
     */
    open && mobile && h('div', {
      key: 'backdrop',
      class: 'pt-veil',
      onclick: event => {
        if (event.target === event.currentTarget) actions.closeMonthPicker();
      },
      style: css({
        position: 'fixed', inset: 0, zIndex: layer, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: '24px',
        background: 'rgba(4,8,12,.72)'
      })
    }, pickerCard('pt-rise')),

    open && !mobile && h('div', {
      key: 'picker',
      style: css({
        position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: layer - 15
      })
    }, pickerCard('pt-pop'))
  );

  /*
   * The entrance differs by where the card appears. Anchored under its trigger
   * it only has to acknowledge the click, so it scales in from just under full
   * size; as a centred overlay on a phone it is covering the screen, and rising
   * says where it came from.
   */
  function pickerCard(motion) {
    return h('div', {
      class: motion,
      style: css({
        width: '268px', padding: '14px', borderRadius: '12px',
        background: C.card, border: '1px solid ' + C.strokeHi,
        boxShadow: '0 18px 40px rgba(0,0,0,.55)',
        display: 'flex', flexDirection: 'column', gap: '10px'
      })
    },
      /*
       * Twelve months in a grid answer "when" and say nothing about "when
       * what". Without a heading the control is a calendar with no subject.
       */
      title && h('div', {
        key: 'title',
        style: css({ display: 'flex', flexDirection: 'column', gap: '4px' })
      },
        kicker(title, { fontSize: '9px', letterSpacing: '1.2px' }),
        note && h('div', {
          key: 'note',
          style: css({ fontSize: '10.5px', lineHeight: 1.4, color: C.faint })
        }, note)
      ),

      h('div', {
        style: css({ display: 'flex', alignItems: 'center', gap: '8px' })
      },
        step(-1, '‹'),
        h('div', {
          style: css({
            flex: 1, textAlign: 'center', fontFamily: MONO, fontSize: '11px',
            letterSpacing: '1px', color: C.text
          })
        }, String(year)),
        step(1, '›')
      ),
      h('div', {
        style: css({ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '6px' })
      }, ...t.months.map((name, index) => tile(index)))
    );
  }
}

export const emptyLine = (text, extra = {}) => h('div', {
  style: css({
    fontSize: '12px', color: C.faint, padding: '2px 0', lineHeight: 1.5, ...extra
  })
}, text);

/**
 * The currency an entry is being made in.
 *
 * Sits beside the amount rather than in a settings screen: the currency belongs
 * to *this* payment, and asking anywhere else invites recording a euro receipt
 * as zloty. The choice sticks, because somebody who paid in euro is usually
 * about to enter several.
 */
export function currencyPicker({ state, actions, style = {} }) {
  const current = actions.entryCurrency();
  const options = [...new Set([
    state.settings.baseCurrency,
    current,
    ...Object.keys(state.data?.rates || {}),
    ...SUGGESTED_CURRENCIES
  ])].filter(Boolean);

  return h('select', {
    onchange: event => actions.setEntryCurrency(event.target.value),
    'aria-label': strings(state.lang).shell.currencyLabel,
    value: current,
    style: css({
      background: C.panel,
      border: '1px solid ' + C.stroke,
      borderRadius: '6px',
      padding: '8px 6px',
      color: C.text,
      fontFamily: MONO,
      fontSize: '11px',
      outline: 'none',
      cursor: 'pointer',
      ...style
    })
  }, ...options.map(code => h('option', { key: code, value: code }, code)));
}

// --------------------------------------------------------------- vehicle ----

/**
 * Rename the car and correct its odometer.
 *
 * The fields are in whatever unit the reader has chosen, while the API stores
 * kilometres — so entry converts on the way in and display converts on the way
 * out. Doing it here, once, is what keeps the two layouts from drifting apart.
 */
/**
 * Whether a fill brought the tank to full.
 *
 * Consumption is measured between two full tanks, so this is the one fact
 * about a fill that decides whether it is a reading at all. Full is the
 * default because it is what nearly every fill is; a top-up is the exception
 * that has to be said.
 */
/**
 * Said at the top of the demo, in so many words.
 *
 * The demo opens on a mortgage, a salary and a car that belong to nobody, and
 * nothing on screen said so -- or what to do after looking around. The only
 * way on was a button at the foot of the sidebar that offered to keep the
 * data, which was the one thing a visitor did not want.
 */
export function demoBanner({ t, actions, mobile = false }) {
  const s = t.shell;
  return h('div', {
    key: 'demo-banner',
    style: css({
      display: 'flex', flexDirection: mobile ? 'column' : 'row',
      alignItems: mobile ? 'stretch' : 'center', gap: mobile ? '10px' : '16px',
      background: C.accentDeep, border: `1px solid ${C.accent}`, borderRadius: '10px',
      padding: mobile ? '12px 14px' : '12px 16px'
    })
  },
    h('div', { style: css({ flex: 1, minWidth: 0 }) },
      h('div', { style: css({ fontSize: '13px', fontWeight: 700, color: C.accentHi }) }, s.demoBanner),
      h('div', {
        style: css({ fontSize: '11.5px', color: C.muted, marginTop: '3px', lineHeight: 1.45 })
      }, s.demoBannerSub)
    ),
    button(s.signUp, actions.openClaim, { style: { flex: '0 0 auto', textAlign: 'center' } })
  );
}

export function fullTankToggle({ full, onToggle, t, compact = false }) {
  const s = t.shell;
  return h('div', {
    ...tap(() => onToggle(!full), full ? s.fuelFull : s.fuelPartial),
    class: 'pt-button',
    role: 'switch',
    'aria-checked': full ? 'true' : 'false',
    title: s.fuelFullNote,
    style: css({
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
      height: compact ? '46px' : '36px', padding: compact ? '0 14px' : '0 11px',
      flex: '0 0 auto', whiteSpace: 'nowrap', cursor: 'pointer',
      fontFamily: MONO, fontSize: '10.5px', letterSpacing: '1px',
      borderRadius: '8px',
      border: `1px solid ${full ? C.accent : C.stroke}`,
      background: full ? C.accentDeep : 'transparent',
      color: full ? C.accentHi : C.muted
    })
  }, (full ? '✓ ' : '◐ ') + (full ? s.fuelFull : s.fuelPartial));
}

export function vehicleForm({ state, metrics, t, fmt, inputs, actions, compact = false }) {
  const car = metrics.vehicle;
  const imperial = fmt.units.system === 'imperial';
  const fromKm = value => (imperial ? kmToMiles(value) : value);
  const s = t.shell;
  const shownVolume = litres => (imperial ? litresToGallons(litres) : litres);
  /* What the field starts with, so an untouched field is recognised as such. */
  const tankText = car.tankLitres ? String(Math.round(shownVolume(car.tankLitres) * 10) / 10) : '';
  const save = () => {
    const name = inputs.value('vName').trim();
    const odo = inputs.number('vOdo');
    const startOdo = inputs.number('vStartOdo');

    // Entered in the reader's units; the store converts. Comparing here in the
    // same units keeps the ordering check honest either way.
    const patch = { name: name || car.name };
    if (odo > 0) patch.odo = odo;
    if (startOdo > 0) patch.startOdo = startOdo;

    /*
     * What it cost and when. Both optional: somebody tracking only fuel should
     * not have to invent a purchase price to save a change of odometer. An
     * emptied date clears rather than keeps, so a wrong one can be taken back.
     */
    const price = inputs.number('vPrice');
    if (price > 0) patch.purchasePrice = price;
    const bought = pickedBought();
    if (bought !== (car.purchaseDate || '')) patch.purchaseDate = bought || null;

    /* Emptied clears, as with the date; untouched sends nothing. */
    const tankNow = inputs.value('vTank').trim();
    if (tankNow !== tankText) {
      const tank = inputs.number('vTank');
      patch.tank = tank > 0 ? tank : null;
    }

    const vin = normaliseVin(inputs.value('vVin'));
    if (vin === null) return actions.showError(s.vinInvalid);
    if (vin !== (car.vin || '')) patch.vin = vin;

    const nextOdo = odo > 0 ? odo : fromKm(car.odoKm);
    const nextStart = startOdo > 0 ? startOdo : fromKm(car.startOdoKm);
    if (nextStart > nextOdo) return actions.showError(t.shell.odometerOrder);

    actions.updateVehicle(patch);
    actions.setSheet(null);
  };

  /*
   * The purchase date, held in state rather than in an input.
   *
   * This one field was still a native `<input type="date">` -- the last one in
   * the app, and the reason `datePicker` was written in the first place. The
   * comment under that function has described the vehicle form as the thing it
   * replaced since the day it was added; it had simply never been pointed at
   * this field. Absent means untouched, so the vehicle's own date stands.
   */
  const pickedBought = () => (
    state.vehicleBought === undefined ? (car.purchaseDate || '') : state.vehicleBought
  );

  /* `text` + a decimal keypad rather than `number` -- see `input` above. */
  const field = (name, placeholder, value, type = 'text') => h('input', {
    ref: inputs.ref(name),
    type: type === 'number' ? 'text' : type,
    inputmode: type === 'number' ? 'decimal' : null,
    autocomplete: type === 'number' ? 'off' : null,
    placeholder,
    value,
    style: css({
      background: C.panel, border: `1px solid ${C.stroke}`, borderRadius: '8px',
      padding: compact ? '0 14px' : '9px 12px',
      height: compact ? '46px' : null,
      color: C.text, fontSize: '12.5px', outline: 'none', minWidth: 0, flex: '1 1 140px'
    })
  });

  return h('div', {
    key: 'vehicle-form',
    style: css({
      background: C.card, border: `1px solid ${C.stroke}`,
      borderRadius: compact ? '14px' : '10px',
      padding: compact ? '14px' : '14px 18px',
      display: 'flex', flexDirection: compact ? 'column' : 'row',
      gap: '8px', alignItems: compact ? 'stretch' : 'center', flexWrap: 'wrap'
    })
  },
    field('vName', t.shell.vehicleName, car.name || ''),
    field('vVin', s.vinLabel, car.vin || ''),
    field('vOdo', `${t.shell.odometer} (${fmt.units.distance})`, Math.round(fromKm(car.odoKm)) || '', 'number'),
    field('vStartOdo', `${t.shell.startOdometer} (${fmt.units.distance})`, Math.round(fromKm(car.startOdoKm)) || '', 'number'),
    field('vTank', `${s.tankLabel} (${fmt.units.volume})`, tankText, 'number'),
    field('vPrice', t.shell.purchasePrice, car.purchasePrice || '', 'number'),
    /*
     * Boxed to match the fields either side of it: the picker's own trigger is
     * a bare label, and standing unframed between two bordered inputs it reads
     * as a caption rather than something to press.
     */
    h('div', {
      style: css({
        background: C.panel, border: `1px solid ${C.stroke}`, borderRadius: '8px',
        padding: compact ? '0 10px' : '0 8px',
        height: compact ? '46px' : '38px',
        display: 'flex', alignItems: 'center', flex: '1 1 140px', minWidth: 0
      })
    },
      datePicker({
        id: 'vehicle-bought',
        value: pickedBought(),
        onPick: stamp => actions.setPickedDate('vehicleBought', stamp),
        label: t.shell.purchaseDate,
        state, actions, t, fmt,
        /* Anchored under the trigger on a desktop; centred over the sheet on a
           phone, where the sheet is itself a fixed overlay. */
        mobile: compact,
        style: { flex: 1, minWidth: 0 }
      })
    ),
    button(t.shell.saveVehicle, save, { style: { flex: compact ? '1 1 auto' : '0 0 auto' } }),
    button(t.shell.cancel, () => actions.setSheet(null), {
      tone: 'muted', style: { flex: '0 0 auto' }
    })
  );
}

/**
 * A day picker in the app's own language, matching {@link monthPicker}.
 *
 * The vehicle form used `<input type="date">`, which behaved correctly and
 * looked like a system control dropped into a designed interface — a different
 * typeface, a different palette, a different idea of what a control is, and a
 * different one again on every browser. This is the same grid the month picker
 * uses, one level finer.
 *
 * That form was the reason this exists and was the last place still using the
 * native control: the replacement had been written but never pointed at it.
 *
 * @param id     unique per picker; shares the month picker's open slot so only
 *               one calendar of either kind can be open at a time
 * @param value  'YYYY-MM-DD' or ''
 * @param onPick called with the chosen 'YYYY-MM-DD'
 */
export function datePicker({
  id, value, onPick, label, title, note, state, actions, t, fmt,
  mobile = false, style = {},
  /* Merged into the button itself, for a form that sets one height for every control. */
  triggerStyle = {},
  /*
   * Which layer the overlay opens on.
   *
   * An edit dialog is itself a fixed overlay at 70, so a picker opened from
   * inside one has to sit above it or it opens behind the form that asked for
   * it -- present, clickable in neither direction, and invisible.
   */
  layer = 55
}) {
  const open = state.monthPicker === id;
  const now = new Date();
  const year = state.monthPickerYear || now.getFullYear();
  const month = state.datePickerMonth || now.getMonth() + 1;

  const stamp = day => `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const daysInMonth = new Date(year, month, 0).getDate();

  /*
   * Monday-first. `getDay()` is Sunday-first, so the shift is not cosmetic: a
   * grid that starts on Sunday puts every Polish date under the wrong heading.
   */
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;

  const step = (by, glyph, onStep) => h('div', {
    ...tap(onStep, String(by)),
    style: css({
      width: '24px', height: '24px', display: 'flex', alignItems: 'center',
      justifyContent: 'center', borderRadius: '7px', cursor: 'pointer',
      border: '1px solid ' + C.stroke, color: C.muted, fontFamily: MONO, fontSize: '11px'
    })
  }, glyph);

  const cell = day => {
    const on = stamp(day) === value;
    return h('div', {
      key: 'd' + day,
      ...tap(() => {
        onPick(stamp(day));
        actions.closeMonthPicker();
      }, String(day)),
      style: css({
        height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '6px', cursor: 'pointer',
        border: '1px solid ' + (on ? C.accent : 'transparent'),
        background: on ? C.line : 'transparent',
        color: on ? C.accent : C.textDim,
        fontFamily: MONO, fontSize: '10.5px'
      })
    }, String(day));
  };

  const grid = () => {
    const cells = [];
    // Leading blanks keep the 1st under its real weekday.
    for (let i = 0; i < firstWeekday; i++) {
      cells.push(h('div', { key: 'pad' + i, style: css({ height: '26px' }) }));
    }
    for (let day = 1; day <= daysInMonth; day++) cells.push(cell(day));
    return cells;
  };

  const pickerCard = motion => h('div', {
    class: motion,
    style: css({
      width: '250px', padding: '14px', borderRadius: '12px',
      background: C.card, border: '1px solid ' + C.strokeHi,
      boxShadow: '0 18px 40px rgba(0,0,0,.55)',
      display: 'flex', flexDirection: 'column', gap: '10px'
    })
  },
    title && h('div', {
      key: 'title',
      style: css({ display: 'flex', flexDirection: 'column', gap: '4px' })
    },
      kicker(title, { fontSize: '9px', letterSpacing: '1.2px' }),
      note && h('div', {
        key: 'note',
        style: css({ fontSize: '10.5px', lineHeight: 1.4, color: C.faint })
      }, note)
    ),

    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px' }) },
      step(-1, '‹', () => actions.stepDatePickerMonth(-1)),
      h('div', {
        style: css({
          flex: 1, textAlign: 'center', fontFamily: MONO, fontSize: '10.5px',
          letterSpacing: '.6px', color: C.text
        })
      }, `${t.months[month - 1]} ${year}`.toUpperCase()),
      step(1, '›', () => actions.stepDatePickerMonth(1))
    ),

    h('div', {
      style: css({ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' })
    },
      ...t.shell.weekdays.map((name, i) => h('div', {
        key: 'w' + i,
        style: css({
          textAlign: 'center', fontFamily: MONO, fontSize: '8.5px',
          letterSpacing: '.4px', color: C.ghost, paddingBottom: '2px'
        })
      }, name))
    ),

    h('div', {
      style: css({ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' })
    }, ...grid()),

    /* Clearing has to be possible: a date typed onto the wrong row is a date. */
    h('div', {
      ...tap(() => { onPick(''); actions.closeMonthPicker(); }, t.shell.jClear),
      style: css({
        textAlign: 'center', padding: '6px', borderRadius: '7px',
        border: '1px solid ' + C.stroke, color: C.muted,
        fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.8px', cursor: 'pointer'
      })
    }, t.shell.jClear)
  );

  return h('div', { style: css({ position: 'relative', ...style }) },
    h('div', {
      ...tap(() => (open ? actions.closeMonthPicker() : actions.openDatePicker(id, value)), label),
      style: css({
        fontFamily: MONO, fontSize: '11px', cursor: 'pointer',
        padding: '8px 10px', borderRadius: '6px',
        border: '1px solid ' + (open ? C.accent : C.stroke),
        background: C.panel,
        color: value ? (open ? C.accent : C.text) : C.ghost,
        whiteSpace: 'nowrap',
        ...triggerStyle
      })
    }, value ? fmt.day(value) : (label || '—')),

    open && mobile && h('div', {
      key: 'backdrop',
      class: 'pt-veil',
      onclick: event => {
        if (event.target === event.currentTarget) actions.closeMonthPicker();
      },
      style: css({
        position: 'fixed', inset: 0, zIndex: layer, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: '24px',
        background: 'rgba(4,8,12,.72)'
      })
    }, pickerCard('pt-rise')),

    open && !mobile && h('div', {
      key: 'picker',
      style: css({
        position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: layer - 15
      })
    }, pickerCard('pt-pop'))
  );
}

// ------------------------------------------------------------ advertising ---

/**
 * The advertising slot, and the consent that gates it.
 *
 * Rendered in exactly one place — the foot of the Overview tab — and never on
 * a screen where somebody is entering money, importing a statement or typing an
 * API key. That is a deliberate limit, not an oversight waiting to be widened.
 *
 * The unit lives inside a sandboxed iframe served from this app's own origin.
 * No ad network script runs in this document: without `allow-same-origin` the
 * frame has an opaque origin and cannot reach the DOM around it, the session
 * token, or the ledger cached in local storage. It is a page of somebody's bank
 * transactions — that isolation is the price of putting advertising on it at all.
 *
 * Returns null whenever there is nothing to show, so callers can drop it into a
 * child list without a guard of their own.
 */
/** How long a frame gets to arrive before the slot gives up on it. */
const AD_FRAME_GRACE_MS = 4000;

export function adSlot({ state, t, actions }) {
  if (state.ads === null) {
    // Asked once, lazily: an instance with no publisher id answers "no" and
    // nothing is ever requested from an ad network.
    actions.loadAds();
    return null;
  }
  if (!state.ads.show) return null;

  const s = t.shell;

  /*
   * Nothing loads until the question is answered. A consent prompt that runs
   * the tracking it is asking about is not a consent prompt.
   */
  if (state.adConsent === null) return adConsentCard(s, actions);

  /*
   * Blocked, unfilled or unreachable — all the same outcome, and the slot goes
   * rather than leaving a labelled empty rectangle that reads as a broken app.
   */
  if (state.adFrame === 'blocked') return null;

  const src = state.ads.frameUrl + (state.adConsent === 'yes' ? '?p=1' : '');

  return h('div', {
    key: 'ad-slot',
    style: css({
      display: 'flex', flexDirection: 'column', gap: '6px',
      border: `1px solid ${C.line}`, borderRadius: '10px',
      background: C.card, padding: '10px 12px', minWidth: 0
    })
  },
    /*
     * Labelled, always. A person looking at their own accounts is entitled to
     * know instantly which rectangle on the screen was paid for.
     */
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px' }) },
      kicker(s.adLabel, { fontSize: '8.5px', letterSpacing: '1.2px' }),
      h('div', { style: css({ flex: 1 }) }),
      h('div', {
        ...tap(() => actions.setAdConsent(null), s.adSettings),
        style: css({
          fontFamily: MONO, fontSize: '9px', color: C.ghost, cursor: 'pointer'
        })
      }, s.adSettings)
    ),
    h('iframe', {
      key: src,
      src,
      title: s.adLabel,
      /*
       * No `allow-same-origin`: that single omission is what gives the frame an
       * opaque origin and keeps it out of this document. `allow-popups` and its
       * escape are what let a click actually reach the advertiser.
       */
      sandbox: 'allow-scripts allow-popups allow-popups-to-escape-sandbox',
      loading: 'lazy',
      referrerpolicy: 'no-referrer',
      /*
       * No `onload` handler: it fires even for a request an extension refused,
       * on the empty document left behind, so it reports success for a frame
       * that never ran. The frame says so itself instead (see the postMessage
       * in its own script); this timer is the deadline for it to do so.
       */
      ref: el => {
        if (el) setTimeout(actions.adFrameBlocked, AD_FRAME_GRACE_MS);
      },
      style: css({
        width: '100%', height: '96px', border: 'none',
        display: 'block', colorScheme: 'normal'
      })
    })
  );
}

const adConsentCard = (s, actions) => h('div', {
  key: 'ad-consent',
  style: css({
    display: 'flex', flexDirection: 'column', gap: '10px',
    border: `1px solid ${C.line}`, borderRadius: '10px',
    background: C.card, padding: '14px 16px'
  })
},
  kicker(s.adLabel, { fontSize: '8.5px', letterSpacing: '1.2px' }),
  h('div', {
    style: css({ fontSize: '12px', color: C.textDim, lineHeight: 1.5 })
  }, s.adConsentBody),
  h('div', { style: css({ display: 'flex', gap: '8px', flexWrap: 'wrap' }) },
    button(s.adConsentNo, () => actions.setAdConsent('no'), {
      tone: 'muted', style: { flex: '1 1 auto' }
    }),
    button(s.adConsentYes, () => actions.setAdConsent('yes'), { style: { flex: '1 1 auto' } })
  )
);

// ---------------------------------------------------------------- alerts ----

const ALERT_TONE = { danger: C.danger, good: C.accent, info: C.accentHi };

/**
 * The engine emits alerts as data (`{kind: 'debt', rate, balance, …}`); this
 * renders them in the reader's language, in the wording each layout has room for.
 */
export function alertText(alert, { t, fmt, mobile = false }) {
  const color = ALERT_TONE[alert.tone] || C.faint;

  switch (alert.kind) {
    case 'debt':
      return {
        color,
        title: t.fx.alertDebtTitle(alert.name),
        body: t.fx.alertDebtBody(
          fmt.number(alert.rate, 1),
          fmt.money(alert.balance),
          fmt.money(alert.monthlyInterest)
        )
      };
    case 'saved':
      return {
        color,
        title: t.fx.alertSaveTitle(fmt.money(alert.amount), alert.month),
        body: mobile
          ? t.fx.alertSaveBody(Math.round(alert.pct * 100))
          : t.fx.alertSaveBody(Math.round(alert.pct * 100), alert.monthsToClearSmallest || null)
      };
    case 'short':
      return {
        color,
        title: t.fx.alertShortTitle,
        body: t.fx.alertShortBody(
          fmt.money(alert.amount),
          alert.topCategory ? (t.cat[alert.topCategory] || alert.topCategory) : '—'
        )
      };
    case 'budget':
      return {
        color,
        title: t.fx.alertBudgetTitle(t.cat[alert.category] || alert.category),
        body: t.fx.alertBudgetBody(fmt.money(alert.over), alert.others)
      };
    case 'vehicle':
    default:
      return {
        color,
        title: t.fx.alertCarTitle(fmt.perDistance(alert.costPerDistance)),
        /* An average that was never measured is left out of the sentence, not
           printed as a dash inside it. */
        body: alert.avgEconomy == null
          ? (mobile
            ? t.fx.alertCarBodyNoEconomy(fmt.money(alert.total))
            : t.fx.alertCarBodyNoEconomy(fmt.money(alert.total), fmt.distance(alert.distance)))
          : (mobile
            ? t.fx.alertCarBody(fmt.money(alert.total), fmt.economy(alert.avgEconomy))
            : t.fx.alertCarBody(
              fmt.money(alert.total),
              fmt.distance(alert.distance),
              fmt.economy(alert.avgEconomy)
            ))
      };
  }
}

/**
 * Income lines the demo seeded carry a `key`; those translate. Anything a
 * person typed has no key and is shown back to them exactly as entered.
 */
export const incomeLabel = (t, row) => (row.key && t.seedLabels[row.key]) || row.label;

/** Expense labels are always one of the known categories, so always translate. */
export const expenseLabel = (t, label) => t.cat[label] || label;

/**
 * The categories to choose from: the fixed ones, then any of the person's own
 * already in use, then the one given -- so editing a row filed under a
 * category of one's own never quietly moves it to the first fixed one.
 */
export function categoryChoices(state, current = null) {
  const own = new Set();
  for (const row of state?.data?.expenses || []) if (row.label && !CATEGORIES.includes(row.label)) own.add(row.label);
  for (const row of state?.data?.recurring || []) if (row.category && !CATEGORIES.includes(row.category)) own.add(row.category);
  if (current && !CATEGORIES.includes(current)) own.add(current);
  return [...CATEGORIES, ...[...own].sort((a, b) => a.localeCompare(b))];
}


/**
 * What an expense row reads as: the description first, the category behind it.
 *
 * A row typed by hand is just its category -- "Groceries", 240 zl -- and that
 * is the whole truth about it. A row from a statement is "ZABKA ZD334 K.1"
 * filed under Groceries, and showing only the category loses the half that
 * says which row this is. So the note leads when there is one, and the
 * category stays alongside it as the quieter half rather than disappearing:
 * the coloured dot alone does not name a category, and a person re-checking
 * how something was filed should not have to decode a swatch.
 */
export const expenseText = (t, row) => {
  const note = expenseNote(t, row);
  return note
    ? { primary: note, secondary: expenseLabel(t, row.label) }
    : { primary: expenseLabel(t, row.label), secondary: null };
};

/*
 * Lines the vehicle tab writes into the ledger carry a code rather than words:
 * a fill-up is noted as 'fuel' and a service as its type ('Maintenance'). The
 * server stores them that way so the ledger does not depend on the language
 * the entry was made in; they are put into words only here, on the way out.
 */
const expenseNote = (t, row) => {
  if (row.origin === 'fuel' && row.note === 'fuel') return t.fuelEntry;
  if (row.origin === 'car_expense' && t.carType[row.note]) return t.carType[row.note];
  return row.note;
};
export const classLabel = (t, cls) => t.cls[cls] || cls;
export const carTypeLabel = (t, type) => t.carType[type] || type;

export const gainColor = value => (value >= 0 ? C.accent : C.danger);

/*
 * The figure over a bar in the twelve-month charts. A Polish unit is a word --
 * "3,8 tys." is nearly twice as wide as "3.8k" -- and on one line it ran into
 * the neighbouring labels, so a word unit goes on a second, smaller line under
 * the number. A one-letter unit stays where it is.
 */
export function barFigure(fmt, value, prefix = '') {
  const { number, unit } = fmt.compactParts(value);
  if (!unit.startsWith('\u00a0')) return prefix + number + unit;
  return [
    h('div', { key: 'number', style: css({ textAlign: 'center' }) }, prefix + number),
    h('div', { key: 'unit', style: css({ textAlign: 'center', fontSize: '8px', lineHeight: 1.1 }) }, unit.trim())
  ];
}

/**
 * What happens to a position you are done with.
 *
 * Pressing x used to delete the row, which took the holding out of every month
 * it had ever been held in: one sale in August rewrote the trend all the way
 * back to the purchase, and nothing asked first. Selling and never having owned
 * it are different claims about the past, so the dialog makes you say which —
 * and both are reversible, which is why deleting still goes through the undo.
 */
/**
 * A live "units x cost = basis" line under the add-holding form.
 *
 * The cost box holds a price per unit, and the only way to find out it was read
 * that way used to be to save the row and read a loss off the table. Showing
 * the multiplication as it is typed makes a total entered by mistake obvious
 * before anything is stored -- 1.3405 x 348 = 466.55 next to a position you
 * paid 348 for is not a subtle wrong answer.
 *
 * Written straight into the node on `input`, and again from the ref on every
 * render, so it survives a re-render without the fields becoming controlled.
 */
export function basisPreview({
  inputs, fmt, unitsField, costField, hintField, currency, priceField, t
}) {
  const money = value => fmt.money(value, Math.abs(value) >= 100 ? 0 : 2, currency);
  /*
   * The price of one unit as it was typed: "10 × €132 = €1,315" for 131.50 read
   * as arithmetic gone wrong. Whole numbers stay whole; a fraction of a cent
   * (a token, a penny stock) keeps four places.
   */
  const perUnit = value => fmt.money(value, Number.isInteger(value) ? 0 : Math.abs(value * 100 - Math.round(value * 100)) > 1e-9 ? 4 : 2, currency);

  /*
   * Both boxes are per unit, and both have now been filled in with a total.
   *
   * The cost box was guarded first, after 1.3405 ounces of silver bought for
   * 348 zl were entered as 348 a unit and reported a loss that never happened.
   * The price box was left alone, and it went the same way: 5.12 shares bought
   * for about 200 zl, with that 200 typed as the price of one share. Every
   * figure downstream was then correct about the wrong number -- 5.12 x 200 is
   * genuinely 1024, and a 412% gain is genuinely what that implies.
   *
   * So both sums are written out as they are typed. Seeing "1 024 zl" appear
   * while entering a 200 zl position is the whole guard: nothing is refused,
   * because a share really can be worth 200, but nothing is silent either.
   */
  const line = (label, units, per) => (
    units === null || per === null || !units || !per
      ? ''
      : `${label}${fmt.number(units)} × ${perUnit(per)} = ${money(units * per)}`
  );

  const update = () => {
    const node = inputs.node(hintField);
    if (!node) return;

    const units = parseNumber(inputs.value(unitsField));
    const cost = parseNumber(inputs.value(costField));
    const price = priceField ? parseNumber(inputs.value(priceField)) : null;

    /* Labelled only when there are two of them; one line needs no heading. */
    const rows = priceField
      ? [
        line(t.costBasis + '  ', units, cost),
        line(t.marketValue + '  ', units, price)
      ].filter(Boolean)
      : [line('', units, cost)].filter(Boolean);

    node.textContent = rows.join('\n');
  };

  const register = inputs.ref(hintField);

  return {
    onInput: update,
    hint: h('div', {
      ref: el => {
        register(el);
        if (el) update();
      },
      style: css({
        fontFamily: MONO, fontSize: '10.5px', color: C.faint,
        minHeight: '13px', lineHeight: 1.35,
        /* Two lines, kept as two: the point is reading them against each
           other, and a wrapped pair reads as one run-on sum. */
        whiteSpace: 'pre-line'
      })
    })
  };
}

/**
 * The sum a position's result is measured against, written out as a sum.
 *
 * The table showed units, price, value and a result, and never the basis those
 * were judged against — so a wrong basis had no symptom other than a result
 * that looked wrong for no reason. `1,3405 × 348 zł` next to a −28% is
 * immediately readable as "but I paid 348 in total", which is the whole point.
 * Clicking it opens the correction.
 */
export function costLine({ row, t, fmt, actions, align = 'flex-end' }) {
  const money = value => fmt.money(value, Math.abs(value) >= 100 ? 0 : 2, row.currency);

  /*
   * Cash says what it earns instead, because that is its equivalent question:
   * a deposit's result is the rate against inflation, the way a share's is the
   * price against what it cost.
   */
  const cash = row.amountOnly;
  if (!cash && !row.units) return null;

  const label = cash ? t.shell.cashEdit : t.shell.costEdit;
  const text = cash
    ? (row.yieldPct
        ? `${fmt.number(row.yieldPct)}% · +${money(row.earned || 0)}`
        : t.shell.cashNoYield)
    : `${fmt.number(row.units)} × ${money(Number(row.cost) || 0)}`;

  const detail = cash
    ? money(row.principal || 0) + ' + ' + t.shell.cashEarned
    : money(row.costBasis || 0);

  return h('div', {
    /* The same correction the pencil opens — this is just the shortcut that
       lands on the figure the reader was already looking at. */
    ...tap(() => actions.askEdit('holding', row.id), label),
    title: label + ' · ' + detail,
    style: css({
      display: 'flex', justifyContent: align, fontFamily: MONO, fontSize: '10px',
      color: C.faint, cursor: 'pointer', whiteSpace: 'nowrap',
      borderBottom: `1px dotted ${C.ghost}`, alignSelf: align, lineHeight: 1.4
    })
  }, text);
}

export function reviewPanel({ state, t, fmt, actions, compact = false }) {
  const s = t.shell;
  if (!state.review) { actions.loadReview(); return null; }
  const r = state.review;
  if (!r.offered) return null;
  const when = iso => s.dateFull(iso);
  const waiting = r.nextAt && Date.parse(r.nextAt) > Date.now();

  return card({ padding: compact ? '14px' : '18px 20px', display: 'flex', flexDirection: 'column', gap: '12px' },
    h('div', { style: css({ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }) },
      h('div', { style: css({ flex: '1 1 240px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }) },
        h('div', { style: css({ fontSize: '14px', fontWeight: 700 }) }, s.rvTitle),
        h('div', { style: css({ fontFamily: MONO, fontSize: '10px', color: C.faint }) }, s.rvSub)
      ),
      r.available && !waiting && h('div', { key: 'ask' },
        button(state.reviewLoading ? s.rvWriting : s.rvAsk, actions.requestReview, {
          style: { opacity: state.reviewLoading ? 0.5 : 1 }
        }))
    ),
    !r.available
      ? h('div', { key: 'plus', style: css({ fontSize: '12px', color: C.muted }) }, s.rvPlus)
      : r.review
        ? h('div', { key: 'text', style: css({ display: 'flex', flexDirection: 'column', gap: '8px' }) },
          h('div', {
            style: css({ fontSize: '13px', lineHeight: 1.6, color: C.text, whiteSpace: 'pre-wrap' })
          }, r.review.text),
          h('div', { style: css({ fontFamily: MONO, fontSize: '10px', color: C.faint }) },
            s.rvWritten(when(r.review.createdAt)) + (waiting ? ' · ' + s.rvNext(when(r.nextAt)) : ''))
        )
        : h('div', { key: 'none', style: css({ fontSize: '12px', color: C.muted, lineHeight: 1.5 }) }, s.rvNone),
    h('div', { style: css({ fontSize: '11px', color: C.faint, lineHeight: 1.45 }) }, s.rvPrivacy)
  );
}

/**
 * New terms, published ahead of their day: what changes, a link, and a way
 * to accept now. Required only from the day itself (the terms gate does that).
 */
export function termsNotice({ state, t, actions, compact = false }) {
  const s = t.shell;
  const upcoming = state.user?.termsUpcoming;
  if (!upcoming || upcoming.accepted || state.user?.isGuest) return null;
  return card({
    padding: compact ? '12px 14px' : '14px 18px', display: 'flex', alignItems: 'center', gap: '12px',
    flexWrap: 'wrap', border: `1px solid ${C.accentDeep}`
  },
    h('div', { style: css({ flex: '1 1 300px', minWidth: 0, fontSize: '12.5px', lineHeight: 1.5 }) },
      /* The day as written (Warsaw), not shifted into UTC. */
      s.tmNotice(s.dateFull(String(upcoming.from).slice(0, 10))), ' ',
      legalLink('terms', state.lang, state.siteBase, s.tmRead, C.accent)
    ),
    button(s.tmAccept, () => actions.acceptTerms({ next: true }), { style: { opacity: state.busy ? 0.5 : 1 } })
  );
}

export function upcomingPanel({ t, fmt, actions, compact = false }) {
  const s = t.shell;
  const { rows, total, days, unconverted } = actions.upcomingCharges();

  /** How far off, in the words a person would use. */
  const when = row => {
    if (row.inDays === null) return s.upcomingSomeTime;
    if (row.inDays <= 0) return s.upcomingToday;
    if (row.inDays === 1) return s.upcomingTomorrow;
    return s.upcomingInDays(row.inDays);
  };

  /*
   * Soon is worth saying loudly; three weeks out is not. The colour is the
   * whole point of the panel -- it is what makes a glance enough.
   */
  const tone = row => {
    if (row.inDays === null) return C.ghost;
    if (row.inDays <= 2) return C.danger;
    if (row.inDays <= 7) return C.accentHi;
    return C.muted;
  };

  const money = row => h('div', {
    style: css({
      fontFamily: MONO, fontSize: compact ? '12px' : '12.5px', flex: '0 0 auto',
      whiteSpace: 'nowrap', textAlign: 'right',
      color: row.kind === 'income' ? C.accent : C.text
    })
  }, (row.kind === 'income' ? '+' : '') + fmt.amount(row.amount, row.currency));

  /** What it is, under the name: where it lands, when, and whether it is entered. */
  const meta = row => [
    /* The category is worth the width on a desktop and is not on a phone, where
       the date and whether it is already entered are what the row is read for. */
    !compact && row.kind === 'expense' ? expenseLabel(t, row.category || 'Other') : null,
    row.date ? fmt.day(row.date) : fmt.monthName(row.month).toUpperCase(),
    /* Said, because otherwise the row reads as money not yet accounted for. */
    row.posted ? s.upcomingPosted : null
  ].filter(Boolean).join(' · ');

  const metaLine = row => h('div', {
    style: css({
      fontFamily: MONO, fontSize: '9.5px', color: C.faint, marginTop: '2px',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
    })
  }, meta(row));

  const name = row => h('div', {
    style: css({
      flex: 1, minWidth: 0, fontSize: compact ? '12.5px' : '13px',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
    })
  }, row.label);

  const countdown = (row, trailing = false) => h('div', {
    style: css({
      ...(trailing
        ? { flex: '0 0 auto', textAlign: 'right' }
        : { width: compact ? '68px' : '86px', flex: `0 0 ${compact ? '68px' : '86px'}` }),
      fontFamily: MONO, fontSize: compact ? '10px' : '10.5px',
      color: tone(row), whiteSpace: 'nowrap',
      overflow: 'hidden', textOverflow: 'ellipsis'
    })
  }, when(row));

  /*
   * Three columns on a desktop, two on a phone.
   *
   * With the amount as a third column of its own, the row needed room for a
   * countdown, a name, a description and a sum before anything could give --
   * and the amount is the one thing that must not be shortened, so on a narrow
   * screen it was what got pushed out. Reported as amounts that "run off and
   * scale badly", which is exactly what an unshrinkable column at the end of an
   * overfull row does.
   *
   * On a phone the amount moves up beside the name instead. The name shortens,
   * the description below it gets the full width, and the sum is pinned to the
   * right edge of a box that cannot be pushed wider than its parent.
   */
  const line = row => (compact
    ? h('div', {
        key: row.id,
        style: css({
          display: 'flex', alignItems: 'flex-start', gap: '9px', padding: '9px 0',
          borderTop: `1px solid ${C.lineSoft}`, minWidth: 0
        })
      },
        countdown(row),
        h('div', { style: css({ flex: 1, minWidth: 0 }) },
          h('div', {
            style: css({ display: 'flex', alignItems: 'baseline', gap: '8px', minWidth: 0 })
          }, name(row), money(row)),
          metaLine(row)
        )
      )
    /*
     * On a desktop every entry is two lines that each end on the right: the
     * name over its amount, the date over how far off it is.
     *
     * The countdown used to open the row, on the left. In the multi-column
     * grid that put each entry's "in 4 days" a few pixels after the previous
     * entry's amount and a whole name-width away from its own -- so the eye
     * read "Medicover 90 zł · in 4 days" for a charge that was due today, and
     * it was reported as the countdown being wrong. The count was right; the
     * layout attached it to the wrong row. Here nothing of one entry sits
     * between another entry's name and amount.
     */
    : h('div', {
        key: row.id,
        style: css({ padding: '10px 0', borderTop: `1px solid ${C.lineSoft}`, minWidth: 0 })
      },
        h('div', {
          style: css({ display: 'flex', alignItems: 'baseline', gap: '10px', minWidth: 0 })
        }, name(row), money(row)),
        h('div', {
          style: css({ display: 'flex', alignItems: 'baseline', gap: '10px', minWidth: 0 })
        },
          h('div', { style: css({ flex: 1, minWidth: 0 }) }, metaLine(row)),
          countdown(row, true)
        )
      ));

  return card(
    { padding: compact ? '14px 16px' : '18px 20px', display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
    h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }) },
      h('div', {
        style: css({ fontSize: '14px', fontWeight: 700, letterSpacing: '-0.2px' })
      }, s.upcoming),
      h('div', {
        style: css({ fontFamily: MONO, fontSize: '10px', color: C.faint })
      }, s.upcomingNote(days))
    ),

    rows.length === 0
      ? emptyLine(s.upcomingEmpty)
      : h('div', { style: css({ display: 'flex', flexDirection: 'column' }) },
          h('div', {
            style: css({
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              gap: '10px', paddingBottom: '10px'
            })
          },
            kicker(s.upcomingLeaving),
            h('div', {
              style: css({
                fontFamily: MONO, fontSize: compact ? '17px' : '20px', color: C.danger
              })
            }, fmt.money(total))
          ),
          /*
           * Two columns where the width is there for them.
           *
           * A ten-row list down one side of a 1600px panel leaves the middle of
           * every row empty and the list itself twice as tall as it needs to be.
           * The grid fills across, so reading left to right still gives the
           * order the rows are sorted in.
           */
          h('div', {
            style: css({
              display: 'grid',
              gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fill, minmax(330px, 1fr))',
              /* Wider than the gap inside an entry, so columns read as columns. */
              columnGap: '44px'
            })
          }, ...rows.map(line)),
          unconverted > 0 && h('div', {
            key: 'unconverted',
            style: css({ fontSize: '10.5px', color: C.muted, paddingTop: '10px' })
          }, s.fixedUnconverted(unconverted))
        )
  );
}

/**
 * Hands a generated file to the device.
 *
 * Saving is a DOM concern, so it lives in the views rather than the store: the
 * store hands back the text and this decides how the device takes it. Share
 * comes first because a download in an iOS web view goes nowhere.
 */
export function saveFile(name, text, type) {
  const blob = new Blob([text], { type: type + ';charset=utf-8' });
  const file = new File([blob], name, { type });

  if (navigator.canShare?.({ files: [file] })) {
    navigator.share({ files: [file], title: name }).catch(() => {
      // Cancelled from the share sheet - nothing was meant to happen.
    });
    return;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  // Revoked on the next frame: revoking immediately races the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Where a legal document lives, in the language on screen, on the site the app
 * belongs to (`state.siteBase` -- the store works it out from the API address).
 */
export function legalUrl(kind, lang, base) {
  const path = lang === 'PL'
    ? (kind === 'terms' ? '/regulamin/' : '/polityka-prywatnosci/')
    : (kind === 'terms' ? '/en/terms/' : '/en/privacy/');
  return base ? new URL(path, base).href : path;
}

/** The main guide on the site, "how to use Portfolio Tracker", in the language on screen. */
export function guideUrl(lang, base) {
  const path = lang === 'PL' ? '/poradniki/jak-korzystac-z-portfolio-tracker/' : '/en/guides/how-to-use-portfolio-tracker/';
  return base ? new URL(path, base).href : path;
}

/**
 * A link to the main guide. In a new tab on the web; in the phone apps, where
 * a new tab is not a thing, in the system browser sheet.
 */
export function guideLink({ state, t }, style, ...children) {
  const url = guideUrl(state.lang, state.siteBase);
  return h('a', {
    key: 'guide',
    class: 'pt-nav',
    href: url,
    target: '_blank',
    rel: 'noopener',
    'aria-label': t.shell.guideAria,
    title: t.shell.guideAria,
    onclick: event => {
      const browser = window.Capacitor?.isNativePlatform?.() && window.Capacitor?.Plugins?.Browser;
      if (!browser) return;
      event.preventDefault();
      browser.open({ url, presentationStyle: 'popover' });
    },
    style: css({ textDecoration: 'none', ...style })
  }, ...children);
}

/* The documents open in a new tab, so reading them loses nothing typed into a form. */
const legalLink = (kind, lang, base, label, color) => h('a', {
  key: kind,
  href: legalUrl(kind, lang, base),
  target: '_blank',
  rel: 'noopener',
  // A link inside the tick box's label opens the document; it does not tick the box.
  onclick: event => event.stopPropagation(),
  style: css({ color, textDecoration: 'underline' })
}, label);

/**
 * The box an account ticks before it exists: on the sign-up form, when a demo
 * becomes an account, and once for an account that never saw either.
 */
export function termsCheck({ checked, onToggle, t, lang, base }) {
  const s = t.shell;
  const [before, terms, between, privacy, after] = s.termsAgree;

  return h('div', {
    key: 'terms-check',
    ...tap(onToggle, s.termsAgree.join('')),
    role: 'checkbox',
    'aria-checked': checked ? 'true' : 'false',
    style: css({ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer', padding: '2px' })
  },
    h('div', {
      style: css({
        width: '18px', height: '18px', flex: '0 0 18px', marginTop: '1px', borderRadius: '5px',
        border: `1px solid ${checked ? C.accent : C.stroke}`,
        background: checked ? C.accent : 'transparent',
        color: C.bg, fontFamily: MONO, fontSize: '11px', fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      })
    }, checked ? '✓' : ''),
    h('div', { style: css({ fontSize: '12px', lineHeight: 1.5, color: C.muted }) },
      before, legalLink('terms', lang, base, terms, C.accent), between,
      legalLink('privacy', lang, base, privacy, C.accent), after)
  );
}

/** The two documents as a quiet line of links. */
export function legalLinks({ t, lang, base }) {
  const s = t.shell;
  return h('div', {
    key: 'legal-links',
    style: css({ fontSize: '11px', lineHeight: 1.5, color: C.faint })
  },
    legalLink('terms', lang, base, s.termsName, C.muted), ' · ',
    legalLink('privacy', lang, base, s.privacyName, C.muted));
}

// ---------------------------------------------------------------- plans ----

/**
 * The free plan's limit on something, or null when nothing limits it right
 * now: before the launch, on Liquidity+, in a demo -- or for a session saved
 * before plans existed, which is treated as unlimited and corrected by the
 * server the moment it matters.
 */
export function planLimit(state, key) {
  const plan = state.user?.plan;
  if (!plan?.enforced) return null;
  const limit = plan.limits?.[key];
  return typeof limit === 'number' ? limit : null;
}

/** Whether holdings may be priced automatically. Not knowing means yes. */
export const quotesAllowed = state => state.user?.plan?.limits?.quotes !== false;

/** Whether the tax summaries are open to this account. Not knowing means yes, as above. */
export const taxesAllowed = state => !state.user?.plan?.enforced || state.user.plan.limits?.taxes !== false;

/**
 * A feature that is part of Liquidity+, in place of the feature: what it does,
 * and the way to the Plan section in Preferences, where it can be bought.
 */
export function planLocked({ t, actions, title, text, compact = false }) {
  const s = t.shell;
  return card({
    padding: compact ? '14px' : '18px 20px', display: 'flex', flexDirection: 'column', gap: '10px',
    border: `1px solid ${C.accentDeep}`
  },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }) },
      h('span', { style: css({ fontFamily: MONO, fontSize: '9.5px', fontWeight: 700, letterSpacing: '1px', color: C.accent }) }, 'LIQUIDITY+'),
      h('span', { style: css({ fontSize: '14px', fontWeight: 700 }) }, title)),
    h('div', { style: css({ fontSize: '12.5px', color: C.textDim, lineHeight: 1.55 }) }, text),
    h('div', { style: css({ display: 'flex' }) }, button(s.planSeePlans, () => actions.openSettings('plan')))
  );
}

/**
 * Trades entered this calendar month -- counted from the journal on screen
 * once it has loaded, so adding or deleting one moves the number at once.
 */
export function journalUsed(state) {
  if (!state.trades) return state.user?.plan?.used?.journalThisMonth || 0;
  const start = monthStart(new Date());
  /* Real trades only: backtest sessions are outside the allowance, as on the server. */
  return state.trades.filter(trade => !trade.backtestId && trade.createdAt && trade.createdAt >= start).length;
}

/**
 * One quiet line about a limit. Once the limit is reached it names Liquidity+,
 * because that is the moment the difference between the plans is the answer.
 */
export function planLine(text, { spent = false, key } = {}) {
  return h('div', {
    key,
    style: css({ fontSize: '11px', lineHeight: 1.45, color: spent ? C.textDim : C.faint })
  },
    spent && h('span', {
      style: css({
        fontFamily: MONO, fontSize: '9px', fontWeight: 700, letterSpacing: '1px',
        color: C.accent, marginRight: '8px'
      })
    }, 'LIQUIDITY+'),
    text
  );
}

export function formDialog({
  key, title, subtitle, rows, note, onSave, onCancel, t, extra = null
}) {
  const s = t.shell;

  return h('div', {
    key,
    class: 'pt-veil',
    onclick: event => { if (event.target === event.currentTarget) onCancel(); },
    style: css({
      position: 'fixed', inset: 0, zIndex: 70, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '20px',
      background: 'rgba(4,8,12,.72)', overflowY: 'auto'
    })
  },
    h('div', {
      class: 'pt-rise', role: 'dialog', 'aria-modal': 'true', 'aria-label': title,
      style: css({
        width: '100%', maxWidth: '420px', background: C.card,
        border: `1px solid ${C.stroke}`, borderRadius: '16px', padding: '20px',
        display: 'flex', flexDirection: 'column', gap: '13px',
        fontFamily: SANS, color: C.text, maxHeight: '86vh', overflowY: 'auto'
      })
    },
      h('div', { style: css({ fontSize: '15px', fontWeight: 700 }) }, title),
      subtitle && h('div', {
        style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.accent })
      }, subtitle),

      ...rows.map((row, i) => h('div', {
        key: 'row-' + i,
        style: css({ display: 'flex', gap: '10px', flexWrap: 'wrap' })
      }, ...row)),

      extra,

      note && h('div', {
        style: css({ fontSize: '11px', color: C.faint, lineHeight: 1.45 })
      }, note),

      h('div', { style: css({ display: 'flex', gap: '8px' }) },
        h('div', {
          ...tap(onSave),
          class: 'pt-button',
          style: css({
            flex: 1, height: '40px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', borderRadius: '10px', background: C.accent,
            color: C.bg, fontFamily: MONO, fontSize: '11px', fontWeight: 700,
            letterSpacing: '.8px', cursor: 'pointer'
          })
        }, s.save),
        h('div', {
          ...tap(onCancel),
          class: 'pt-button',
          style: css({
            flex: '0 0 110px', height: '40px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', borderRadius: '10px', border: `1px solid ${C.stroke}`,
            color: C.muted, fontFamily: MONO, fontSize: '11px', fontWeight: 700,
            letterSpacing: '.8px', cursor: 'pointer'
          })
        }, s.cancel)
      )
    )
  );
}

/** One labelled field inside a {@link formDialog} row. Dated fields use
    {@link dialogDate} instead, which opens the app's own calendar. */
export function dialogField(inputs, name, label, seed, options = {}) {
  return h('div', {
    key: name,
    style: css({
      display: 'flex', flexDirection: 'column', gap: '5px',
      flex: options.flex || '1 1 130px', minWidth: 0
    })
  },
    h('div', {
      style: css({
        fontSize: '10.5px', color: C.faint, letterSpacing: '.4px', textTransform: 'uppercase'
      })
    }, label),
    options.select
      ? select(inputs, name, options.select, { height: '38px', width: '100%' },
          { value: seed, onChange: options.onChange, ariaLabel: label })
      : input(inputs, name, label, {
          type: options.type || 'text',
          value: seed,
          style: { fontFamily: MONO, fontSize: '12.5px', width: '100%' },
          onInput: options.onInput,
          onEnter: options.onEnter
        })
  );
}

/*
 * Every row in this app can be corrected, and they all come through here.
 *
 * The shape is the same each time — read the row out of state by the id in
 * `state.editing`, seed a form from it, validate, hand a whole patch back — so
 * what differs is only which fields a kind of row actually has.
 *
 * Dates are picked, not typed. The app's own pickers are popovers anchored on
 * `state.monthPicker`, and nesting one inside a modal that is itself fixed and
 * scrolling is a fight not worth having -- so these use the browser's, which
 * has no such problem because the calendar it opens is not in the page at all.
 * Every dated field here stores an ISO string, which is exactly what a native
 * picker reads and writes.
 */

const editedRow = (state, list) => {
  const id = state.editing?.id;
  return id ? (list || []).find(row => row.id === id) : null;
};

const asText = (value, places = 6) => (
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? ''
    : String(Number(Number(value).toFixed(places)))
);
/* With the decimal comma every other form in the app writes ("84,37"); the dialogs alone wrote 84.37. */
const asMoney = value => numberText(asText(value, 2));

/**
 * A dated field inside a dialog, using the app's own calendar.
 *
 * The browser's native `<input type="date">` was the obvious answer and it was
 * the wrong one: the calendar it opens is an operating-system window that page
 * CSS cannot reach, so it arrives in the platform's colours and typeface in the
 * middle of a dark monospaced app. `color-scheme` darkens it and nothing else.
 *
 * The app already has a calendar that looks like the app. The reason the
 * dialogs had typed boxes instead was the popover: anchored absolutely under
 * its trigger, it fights a modal that is itself fixed and scrolling. Opened as
 * a centred overlay ABOVE the dialog -- which is the presentation it already
 * uses on a phone, for the same reason -- that fight disappears.
 *
 * The value lives in `state.editDates` rather than in the DOM, because a picker
 * has no input to read back from.
 */
function dialogDate(ctx, name, label, seed, { month = false, flex } = {}) {
  const { state, actions, t, fmt } = ctx;
  const value = editDate(state, name, seed);
  const pick = month ? monthPicker : datePicker;

  return h('div', {
    key: name,
    style: css({
      display: 'flex', flexDirection: 'column', gap: '5px',
      flex: flex || '1 1 130px', minWidth: 0
    })
  },
    h('div', {
      style: css({
        fontSize: '10.5px', color: C.faint, letterSpacing: '.4px', textTransform: 'uppercase'
      })
    }, label),
    h('div', {
      style: css({
        height: '38px', display: 'flex', alignItems: 'center', padding: '0 10px',
        background: C.panel, border: `1px solid ${C.stroke}`, borderRadius: '6px'
      })
    },
      pick({
        id: 'edit-' + name,
        value,
        onPick: picked => actions.setEditDate(name, picked),
        label,
        state, actions, t, fmt,
        /* Centred over the dialog, never anchored inside it. */
        mobile: true,
        layer: 80,
        style: { flex: 1, minWidth: 0 }
      })
    )
  );
}

/**
 * What a dialog's dated field currently holds.
 *
 * Absent means untouched, so the row's own value stands. An empty string is a
 * real answer -- somebody cleared the date -- and has to survive as one.
 */
export function editDate(state, name, seed) {
  const picked = state.editDates ? state.editDates[name] : undefined;
  return picked === undefined ? (seed || '') : picked;
}

/** The currencies worth offering: the row's own first, then what is in use. */
export const currencyOptions = (state, own) => [...new Set([
  own, state.settings.baseCurrency,
  ...Object.keys(state.data?.rates || {}), ...SUGGESTED_CURRENCIES
])].filter(Boolean).map(code => ({ value: code, label: code }));

/**
 * Corrects a position that is already on the books — all of it, not one field.
 *
 * Replaces two narrower dialogs that had grown up beside each other, one for
 * the cost basis and one for a cash balance's rate. Three overlapping ways to
 * edit one row is worse than one that covers it — and a ticker, a name, a class
 * or a currency entered wrongly had no way to be corrected at all: the only
 * route was to delete the position, losing the month it was held from and every
 * price recorded against it.
 *
 * The form changes shape with the class, exactly as the add form does, because
 * a balance has an amount and a rate where a share has a quantity and a price.
 */
export function holdingEditDialog(ctx) {
  const { state, t, fmt, inputs, actions } = ctx;
  const row = editedRow(state, state.data?.holdings);
  if (!row) return null;

  const s = t.shell;
  const cls = state.editClass || row.cls || 'Equity / ETF';
  const cash = isAmountOnly(cls);

  const read = name => parseNumber(inputs.value(name));
  const write = (name, value, format = asText) => {
    const node = inputs.node(name);
    if (node) node.value = format(value);
  };

  /*
   * The per-unit cost and the total paid, kept in step.
   *
   * Both are offered because both are things people actually know: a share
   * bought at 41.20 each, a bag of silver bought for 348 all in. What gets
   * stored is the per-unit figure, and a total typed into that box is counted
   * once per unit — which is how a position that was roughly flat came to
   * report a 28% loss.
   */
  const fromPerUnit = () => {
    const units = read('ehUnits');
    const per = read('ehCost');
    write('ehTotal', units === null || per === null ? null : units * per, asMoney);
  };
  const fromTotal = () => {
    const units = read('ehUnits');
    const total = read('ehTotal');
    write('ehCost', units === null || total === null || units === 0 ? null : total / units);
  };

  /*
   * The same pair again, for the price side.
   *
   * The cost box was given a total to type into after somebody entered 348 zl
   * for a bag of silver as 348 an ounce. The price box had no such pair, and it
   * went the same way: 5.12 shares bought for about 200 zl, with the 200 typed
   * as the price of one share. Nothing downstream could tell -- 5.12 x 200 is
   * genuinely 1024, and 412% is genuinely what that implies against a 200 zl
   * basis. The only place to catch it is the box it was typed into.
   */
  const fromPrice = () => {
    const units = read('ehUnits');
    const price = read('ehPrice');
    write('ehValue', units === null || price === null ? null : units * price, asMoney);
  };
  const fromValue = () => {
    const units = read('ehUnits');
    const value = read('ehValue');
    write('ehPrice', units === null || value === null || units === 0 ? null : value / units);
  };
  /* Units feed both pairs, so a corrected quantity restates both totals. */
  const fromUnits = () => { fromPerUnit(); fromPrice(); };

  const save = () => {
    const ticker = inputs.value('ehTicker').trim().toUpperCase();
    if (!ticker) return actions.showError(s.tickerNeeded);

    const when = editDate(state, 'ehBought', row.boughtOn);
    if (when && !isDate(when)) return actions.showError(s.dateNeeded);

    const patch = {
      ticker,
      name: inputs.value('ehName').trim() || ticker,
      cls,
      currency: inputs.value('ehCurrency') || row.currency,
      /* Empty clears a day entered wrongly; the API keeps those apart. */
      boughtOn: when || null
    };

    if (cash) {
      const amount = read('ehAmount');
      const rate = read('ehYield');
      if (amount === null || amount < 0) return actions.showError(s.amountNeeded);
      if (rate !== null && rate < 0) return actions.showError(s.numberNeeded);
      Object.assign(patch, {
        units: 1, cost: amount, price: amount, yieldPct: rate === null ? 0 : rate
      });
    } else {
      const units = read('ehUnits');
      const cost = read('ehCost');
      const price = read('ehPrice');
      if (units === null || price === null || units < 0 || price < 0) {
        return actions.showError(s.numberNeeded);
      }
      Object.assign(patch, {
        units,
        cost: cost === null || cost < 0 ? price : cost,
        price,
        /* Leaving a class behind takes its figure with it. */
        yieldPct: 0
      });
    }

    actions.saveEdit('holding', row.id, patch);
  };

  const rows = [
    [
      dialogField(inputs, 'ehTicker', t.phTicker, row.ticker, { flex: '0 0 110px' }),
      dialogField(inputs, 'ehName', t.phName, row.name)
    ],
    [
      dialogField(inputs, 'ehClass', s.classLabel, cls, {
        select: ASSET_CLASSES.map(key => ({ value: key, label: classLabel(t, key) })),
        onChange: actions.setEditClass
      }),
      dialogField(inputs, 'ehCurrency', s.currencyLabel, row.currency, {
        select: currencyOptions(state, row.currency), flex: '0 0 110px'
      })
    ],
    [
      /* Optional, and the only thing that reads it is the calendar. Empty
         leaves the position listed under the grid rather than on a day. */
      dialogDate(ctx, 'ehBought', t.phBoughtOn, row.boughtOn, { flex: '0 0 170px' })
    ]
  ];

  if (cash) {
    /*
     * What the balance is worth, not what one unit of it cost.
     *
     * A position already filed as cash holds its amount in `price` — that is
     * how a balance is stored. One being converted holds a price per unit, and
     * seeding from it turned a holding of 41 units at 700 into a balance of
     * 700: twenty-eight thousand gone, silently, on a change of class.
     */
    const amountSeed = isAmountOnly(row.cls)
      ? Number(row.price)
      : Number(row.units) * Number(row.price);

    rows.push([
      dialogField(inputs, 'ehAmount', s.cashAmount, asMoney(amountSeed), {
        type: 'number', onEnter: save
      }),
      dialogField(inputs, 'ehYield', s.cashRate, asText(row.yieldPct || 0), {
        type: 'number', onEnter: save
      })
    ]);
  } else {
    rows.push([
      dialogField(inputs, 'ehUnits', s.costUnits, asText(row.units), {
        type: 'number', onInput: fromUnits
      })
    ]);
    rows.push([
      dialogField(inputs, 'ehPrice', t.phPrice, asText(row.price), {
        type: 'number', onInput: fromPrice
      }),
      dialogField(inputs, 'ehValue', s.positionValue, asMoney(Number(row.units) * Number(row.price)), {
        type: 'number', onInput: fromValue
      })
    ]);
    rows.push([
      dialogField(inputs, 'ehCost', s.costPerUnit, asText(row.cost), {
        type: 'number', onInput: fromPerUnit
      }),
      dialogField(inputs, 'ehTotal', s.costTotal, asMoney(Number(row.units) * Number(row.cost)), {
        type: 'number', onInput: fromTotal, onEnter: save
      })
    ]);
  }

  return formDialog({
    key: 'holding-edit-' + row.id,
    title: s.editHoldingTitle,
    subtitle: row.ticker + ' · ' + classLabel(t, row.cls),
    rows,
    note: cash ? s.cashYieldNote : s.costNote,
    onSave: save,
    onCancel: actions.closeEdit,
    t
  });
}

/**
 * Corrects a debt that is already on the books.
 *
 * There was no way to do this at all. The API has accepted a patch since the
 * beginning and nothing ever sent one, so a rate typed wrongly, an instalment
 * that changed, or a lender renamed meant deleting the loan — and its payment
 * history went with it.
 */
/* The debt chart's past months: the danger red, dimmed the way accentDeep dims the green. */
export const DEBT_DEEP = '#5A2420';

/*
 * A net worth below zero is drawn in the debt colours. It was green whatever
 * its sign, so an account that owed more than it held showed "-4,150" in the
 * colour everywhere else used for money that is yours.
 */
export const netColor = value => (value < 0 ? C.danger : C.accent);
export const netBarFill = point => (
  point.net < 0 ? (point.current ? C.danger : DEBT_DEEP) : (point.current ? C.accent : C.accentDeep)
);

/**
 * How far one debt moved since the month before, said beside its balance.
 *
 * Down is the good direction here, the other way round from every other
 * figure in the app, so the colours are too.
 */
export function debtChange(row, prevMonth, t, fmt) {
  if (row.change === null || row.change === undefined) return { text: t.fx.debtFirst, color: C.faint };
  if (row.balance === 0 && row.change < 0) return { text: t.fx.debtCleared, color: C.accent };
  if (Math.abs(row.change) < 0.005) return { text: t.fx.debtSame(prevMonth), color: C.faint };
  return {
    text: t.fx.vsMonth(fmt.signed(row.change, row.currency), prevMonth),
    color: row.change < 0 ? C.accent : C.danger
  };
}

export function debtEditDialog({ state, t, fmt, inputs, actions }) {
  const row = editedRow(state, state.data?.debts);
  if (!row) return null;

  const s = t.shell;
  const read = name => parseNumber(inputs.value(name));
  /*
   * The balance as it stood in the month on screen -- what the dialog shows,
   * and what a change to it is a statement about.
   */
  const shown = debtBalanceAt(row, state.month) ?? debtLatestBalance(row);

  const save = () => {
    const name = inputs.value('edName').trim();
    const balance = read('edBalance');
    if (!name) return actions.showError(s.labelNeeded);
    if (balance === null || balance < 0) return actions.showError(s.amountNeeded);

    const rate = read('edRate');
    const payment = read('edPayment');
    const original = read('edOriginal');
    const fees = read('edFees');
    const term = read('edTerm');
    /* The day the instalment is due -- what the calendar export needs; empty clears it. */
    const payDay = read('edPayDay');

    for (const value of [rate, payment, original, fees, term]) {
      if (value !== null && value < 0) return actions.showError(s.numberNeeded);
    }
    if (payDay !== null && (payDay < 1 || payDay > 31 || Math.round(payDay) !== payDay)) return actions.showError(s.dbPayDayNeeded);

    const patch = {
      name,
      original: original === null ? balance : original,
      rate: rate === null ? 0 : rate,
      payment: payment === null ? 0 : payment,
      fees: fees === null ? 0 : fees,
      /* An empty term clears one entered wrongly; the API keeps those apart. */
      term: term === null || term <= 0 ? null : Math.round(term),
      payDay: payDay || null,
      currency: inputs.value('edCurrency') || row.currency
    };
    /*
     * Sent only when it changed. Renaming a loan while looking at March says
     * nothing about what was owed in March, and sending the figure anyway
     * would file one there.
     */
    if (Math.abs(balance - shown) >= 0.005) patch.balance = balance;
    actions.saveEdit('debt', row.id, patch);
  };

  return formDialog({
    key: 'debt-edit-' + row.id,
    title: s.editDebtTitle,
    subtitle: row.name,
    rows: [
      [
        dialogField(inputs, 'edName', t.phLender, row.name),
        dialogField(inputs, 'edCurrency', s.currencyLabel, row.currency, {
          select: currencyOptions(state, row.currency), flex: '0 0 100px'
        })
      ],
      [
        dialogField(inputs, 'edBalance', s.debtBalanceIn(fmt.monthName(state.month)), asMoney(shown), {
          type: 'number'
        }),
        dialogField(inputs, 'edOriginal', t.phOriginal, asMoney(row.original), { type: 'number' })
      ],
      [
        dialogField(inputs, 'edRate', t.phApr, asText(row.rate, 3), { type: 'number' }),
        dialogField(inputs, 'edPayment', t.phPayment, asMoney(row.payment), { type: 'number' })
      ],
      [
        dialogField(inputs, 'edTerm', t.phTerm, row.term ? String(row.term) : '', { type: 'number' }),
        dialogField(inputs, 'edFees', t.phFees, asMoney(row.fees || 0), { type: 'number' })
      ],
      [
        dialogField(inputs, 'edPayDay', s.dbPayDayF, row.payDay ? String(row.payDay) : '', { type: 'number', onEnter: save })
      ]
    ],
    note: s.editDebtNote,
    onSave: save,
    onCancel: actions.closeEdit,
    t
  });
}

/**
 * Corrects a line in the ledger — money in or money out.
 *
 * The month is a field rather than a fixture: filing something in the wrong
 * month is the commonest mistake there is, and the only remedy was to delete
 * the line, navigate, and type it again.
 */
export function entryEditDialog(ctx) {
  const { state, t, fmt, inputs, actions } = ctx;
  const income = state.editing?.kind === 'income';
  const row = editedRow(state, income ? state.data?.income : state.data?.expenses);
  if (!row) return null;

  const s = t.shell;

  const save = () => {
    const label = inputs.value('enLabel').trim();
    const amount = parseNumber(inputs.value('enAmount'));
    const when = editDate(state, 'enMonth', row.month || state.month);

    if (!label) return actions.showError(s.labelNeeded);
    if (amount === null || amount < 0) return actions.showError(s.amountNeeded);
    if (!isMonth(when)) return actions.showError(s.monthNeeded);

    actions.saveEdit(income ? 'income' : 'expense', row.id, {
      month: when,
      label,
      amount,
      currency: inputs.value('enCurrency') || row.currency,
      ...(income ? {} : { note: inputs.value('enNote').trim() })
    });
  };

  const rows = [
    [
      income
        ? dialogField(inputs, 'enLabel', t.phSource, row.label)
        : dialogField(inputs, 'enLabel', s.categoryLabel, row.label, {
            select: categoryChoices(state, row.label).map(key => ({ value: key, label: expenseLabel(t, key) }))
          }),
      dialogField(inputs, 'enCurrency', s.currencyLabel, row.currency, {
        select: currencyOptions(state, row.currency), flex: '0 0 100px'
      })
    ],
    [
      dialogField(inputs, 'enAmount', t.phAmount, asMoney(row.amount), { type: 'number' }),
      dialogDate(ctx, 'enMonth', s.monthLabel, row.month || state.month, {
        month: true, flex: '0 0 150px'
      })
    ]
  ];

  if (!income) {
    rows.push([dialogField(inputs, 'enNote', t.phNote, row.note || '', { onEnter: save })]);
  }

  return formDialog({
    key: 'entry-edit-' + row.id,
    title: income ? s.editIncomeTitle : s.editExpenseTitle,
    subtitle: row.label,
    rows,
    note: s.editEntryNote,
    onSave: save,
    onCancel: actions.closeEdit,
    t
  });
}

/**
 * Corrects a fill-up.
 *
 * The odometer matters twice over: it is this fill's reading and the baseline
 * the next one measures its economy against, so a digit typed wrongly reports a
 * consumption figure for two fills, not one. Nothing but the date could be
 * changed before.
 */
export function fuelEditDialog(ctx) {
  const { state, t, fmt, inputs, actions } = ctx;
  const row = editedRow(state, state.data?.fuel);
  if (!row) return null;

  const s = t.shell;
  const read = name => parseNumber(inputs.value(name));
  const isFull = () => (state.editFuelFull ?? (row.full !== false));

  /*
   * In the reader's units, both ways. The dialog used to show the stored
   * kilometres and litres under "mi" and "gal", and to store whatever was typed
   * back as kilometres and litres. A field left as it was keeps the stored
   * figure exactly, rather than a round trip through miles.
   */
  const imperial = fmt.units.system === 'imperial';
  const odoText = asText(imperial ? kmToMiles(row.odo) : row.odo, 1);
  const volumeText = asText(imperial ? litresToGallons(row.gal) : row.gal, 2);
  const kept = (name, text) => inputs.value(name).trim() === String(text);

  const save = () => {
    const odo = read('fuOdo');
    const volume = read('fuVolume');
    const cost = read('fuCost');
    const when = editDate(state, 'fuDate', row.date);

    if (odo === null || volume === null || odo < 0 || volume <= 0) {
      return actions.showError(s.fuelNeeded);
    }
    if (cost !== null && cost < 0) return actions.showError(s.numberNeeded);
    if (when && !isDate(when)) return actions.showError(s.dateNeeded);

    const tankLitres = state.data?.vehicle?.tank || null;
    const tank = tankLitres && (imperial ? litresToGallons(tankLitres) : tankLitres);
    if (tank && volume > tank * 1.15) {
      return actions.showError(s.fillTooBig(fmt.number(Math.round(tank * 10) / 10) + ' ' + fmt.units.volume));
    }

    actions.saveEdit('fuel', row.id, {
      odo: kept('fuOdo', odoText) ? row.odo : (imperial ? milesToKm(odo) : odo),
      gal: kept('fuVolume', volumeText) ? row.gal : (imperial ? gallonsToLitres(volume) : volume),
      cost: cost === null ? 0 : cost,
      full: isFull(),
      ...(when ? { date: when } : {})
    });
  };

  return formDialog({
    key: 'fuel-edit-' + row.id,
    title: s.editFuelTitle,
    subtitle: fmt.date ? fmt.date(row.date) : row.date,
    rows: [
      [
        dialogField(inputs, 'fuOdo', fmt.units.distance, odoText, { type: 'number' }),
        dialogField(inputs, 'fuVolume', fmt.units.volume, volumeText, { type: 'number' })
      ],
      [
        dialogField(inputs, 'fuCost', t.phCost, asMoney(row.cost || 0), { type: 'number' }),
        dialogDate(ctx, 'fuDate', s.dateLabel, row.date, { flex: '0 0 170px' })
      ],
      [fullTankToggle({ full: isFull(), onToggle: actions.setEditFuelFull, t })]
    ],
    note: s.editFuelNote,
    onSave: save,
    onCancel: actions.closeEdit,
    t
  });
}

/**
 * Corrects a service or a running charge.
 *
 * Its mirrored line in the ledger follows: the repository moves that line when
 * the date changes and rewrites it when the amount does, so the two halves stay
 * one transaction.
 */
export function carExpenseEditDialog(ctx) {
  const { state, t, fmt, inputs, actions } = ctx;
  const row = editedRow(state, state.data?.carEx);
  if (!row) return null;

  const s = t.shell;

  const save = () => {
    const amount = parseNumber(inputs.value('cxAmount'));
    const when = editDate(state, 'cxDate', row.date);
    if (amount === null || amount < 0) return actions.showError(s.amountNeeded);
    if (when && !isDate(when)) return actions.showError(s.dateNeeded);

    actions.saveEdit('carEx', row.id, {
      type: inputs.value('cxType') || row.type,
      note: inputs.value('cxNote').trim(),
      amount,
      ...(when ? { date: when } : {})
    });
  };

  return formDialog({
    key: 'carex-edit-' + row.id,
    title: s.editCarExpenseTitle,
    subtitle: carTypeLabel(t, row.type),
    rows: [
      [
        dialogField(inputs, 'cxType', s.typeLabel, row.type, {
          select: CAR_EXPENSE_TYPES.map(key => ({ value: key, label: carTypeLabel(t, key) }))
        }),
        dialogDate(ctx, 'cxDate', s.dateLabel, row.date, { flex: '0 0 170px' })
      ],
      [
        dialogField(inputs, 'cxNote', t.phNote, row.note || ''),
        dialogField(inputs, 'cxAmount', t.phAmount, asMoney(row.amount), {
          type: 'number', flex: '0 0 120px', onEnter: save
        })
      ]
    ],
    note: s.editCarExpenseNote,
    onSave: save,
    onCancel: actions.closeEdit,
    t
  });
}

/**
 * Corrects a logged trade.
 *
 * The journal is a record kept to be compared with itself later, so a mistyped
 * result is worse here than anywhere: it does not merely misreport one trade,
 * it moves the win rate and the followed-rules figure that the whole point of
 * keeping it rests on.
 */
export function tradeEditDialog(ctx) {
  const { state, t, fmt, inputs, actions } = ctx;
  const row = editedRow(state, state.trades);
  if (!row) return null;

  const s = t.shell;
  const optional = values => [
    { value: '', label: s.jUnset },
    ...values.map(key => ({ value: key, label: key }))
  ];

  const save = () => {
    const pair = inputs.value('trPair').trim();
    if (!pair) return actions.showError(s.pairNeeded);

    const lots = parseNumber(inputs.value('trLots'));
    const netPl = parseNumber(inputs.value('trPl'));
    if (lots !== null && lots < 0) return actions.showError(s.numberNeeded);

    actions.saveTradeEdit(row.id, {
      date: editDate(state, 'trDate', row.date) || row.date,
      pair,
      session: inputs.value('trSession'),
      timeFrame: inputs.value('trTf'),
      setup: inputs.value('trSetup'),
      side: inputs.value('trSide') || 'LONG',
      lots: lots === null ? 0 : lots,
      exitLogic: inputs.value('trExit'),
      risk: inputs.value('trRisk'),
      rulesFollowed: inputs.value('trRules') === 'yes',
      moneyManagement: inputs.value('trMm') === 'yes',
      netPl: netPl === null ? 0 : netPl,
      currency: inputs.value('trCurrency') || row.currency,
      reportUrl: inputs.value('trReport').trim()
    });
  };

  return formDialog({
    key: 'trade-edit-' + row.id,
    title: s.editTradeTitle,
    subtitle: row.pair + ' · ' + (row.date || ''),
    rows: [
      [
        dialogField(inputs, 'trPair', s.jPair, row.pair, { flex: '0 0 120px' }),
        dialogDate(ctx, 'trDate', s.dateLabel, row.date, { flex: '0 0 170px' })
      ],
      [
        dialogField(inputs, 'trSide', s.jSide, row.side || 'LONG', {
          select: TRADE_SIDES.map(key => ({ value: key, label: key })), flex: '0 0 110px'
        }),
        dialogField(inputs, 'trLots', s.jLots, asText(row.lots, 2), { type: 'number', flex: '0 0 100px' }),
        dialogField(inputs, 'trPl', s.jNetPl, asMoney(row.netPl), { type: 'number' }),
        dialogField(inputs, 'trCurrency', s.jCurrency, row.currency || 'USD', {
          select: [row.currency || 'USD', ...SUGGESTED_CURRENCIES.filter(code => code !== (row.currency || 'USD'))]
            .map(code => ({ value: code, label: code })),
          flex: '0 0 90px'
        })
      ],
      [
        dialogField(inputs, 'trSession', s.jSession, row.session || ''),
        dialogField(inputs, 'trTf', s.jTimeFrame, row.timeFrame || '')
      ],
      [
        dialogField(inputs, 'trSetup', s.jSetup, row.setup || ''),
        dialogField(inputs, 'trExit', s.jExit, row.exitLogic || '', {
          select: optional(TRADE_EXITS), flex: '0 0 130px'
        })
      ],
      [
        dialogField(inputs, 'trRisk', s.jRisk, row.risk || '', {
          select: optional(TRADE_RISKS), flex: '0 0 120px'
        }),
        dialogField(inputs, 'trRules', s.jRules, row.rulesFollowed ? 'yes' : 'no', {
          select: [{ value: 'yes', label: s.jYesShort }, { value: 'no', label: s.jNoShort }],
          flex: '0 0 100px'
        }),
        dialogField(inputs, 'trMm', s.jMm, row.moneyManagement ? 'yes' : 'no', {
          select: [{ value: 'yes', label: s.jYesShort }, { value: 'no', label: s.jNoShort }],
          flex: '0 0 100px'
        })
      ],
      [dialogField(inputs, 'trReport', s.jReport, row.reportUrl || '', { onEnter: save })]
    ],
    note: s.editTradeNote,
    onSave: save,
    onCancel: actions.closeEdit,
    t
  });
}

/** Picks whichever dialog the open row calls for. */
/**
 * Corrects a fixed cost — including how often it repeats and when it ends.
 *
 * Cancelling a subscription is an end date, not a deletion: deleting the
 * template takes the entries it already posted out of the record along with it,
 * so a year of paying for something would vanish from the months it was paid
 * in. Setting the month it ends leaves that history alone and simply stops it
 * asking.
 */
export function recurringEditDialog(ctx) {
  const { state, t, fmt, inputs, actions } = ctx;
  const row = editedRow(state, state.data?.recurring);
  if (!row) return null;

  const s = t.shell;

  const save = () => {
    const label = inputs.value('rcLabel').trim();
    const amount = parseNumber(inputs.value('rcAmount'));
    const every = Number(inputs.value('rcEvery')) || 1;
    const from = editDate(state, 'rcFrom', row.firstMonth);
    const until = editDate(state, 'rcUntil', row.untilMonth);
    const dayText = inputs.value('rcDay').trim();
    const day = dayText ? parseNumber(dayText) : null;

    if (!label) return actions.showError(s.labelNeeded);
    if (amount === null || amount <= 0) return actions.showError(s.amountNeeded);
    if (from && !isMonth(from)) return actions.showError(s.monthNeeded);
    if (until && !isMonth(until)) return actions.showError(s.monthNeeded);
    if (dayText && (day === null || day < 1 || day > 31)) {
      return actions.showError(s.amountNeeded);
    }

    actions.saveEdit('recurring', row.id, {
      label,
      amount,
      currency: inputs.value('rcCurrency') || row.currency,
      ...(row.kind === 'expense'
        ? { category: inputs.value('rcCategory') || 'Other' }
        : {}),
      everyMonths: every,
      /* Empty clears rather than keeps: an end date entered by mistake has to
         have a way back off the row. */
      firstMonth: from || null,
      untilMonth: until || null,
      dayOfMonth: day === null ? null : Math.round(day)
    });
  };

  return formDialog({
    key: 'recurring-edit-' + row.id,
    title: s.editFixedTitle,
    subtitle: row.label,
    rows: [
      [
        dialogField(inputs, 'rcLabel', t.phSource, row.label),
        dialogField(inputs, 'rcCurrency', s.currencyLabel, row.currency, {
          select: currencyOptions(state, row.currency), flex: '0 0 100px'
        })
      ],
      [
        dialogField(inputs, 'rcAmount', t.phAmount, asMoney(row.amount), { type: 'number' }),
        dialogField(inputs, 'rcEvery', s.fixedEvery, String(row.everyMonths || 1), {
          select: CADENCES.map(every => ({ value: String(every), label: s.cadence[every] })),
          flex: '0 0 140px'
        })
      ],
      row.kind === 'expense' ? [
        dialogField(inputs, 'rcCategory', s.categoryLabel, row.category || 'Other', {
          select: categoryChoices(state, row.category).map(key => ({ value: key, label: expenseLabel(t, key) }))
        })
      ] : [],
      [
        dialogDate(ctx, 'rcFrom', s.fixedFrom, row.firstMonth, { month: true, flex: '1 1 140px' }),
        dialogDate(ctx, 'rcUntil', s.fixedUntil, row.untilMonth, { month: true, flex: '1 1 140px' }),
        dialogField(inputs, 'rcDay', t.phDay, row.dayOfMonth ? String(row.dayOfMonth) : '', {
          type: 'number', flex: '0 0 70px', onEnter: save
        })
      ]
    ],
    note: s.fixedUntilNone,
    onSave: save,
    onCancel: actions.closeEdit,
    t
  });
}

export function editDialog(ctx) {
  switch (ctx.state.editing?.kind) {
    case 'holding': return holdingEditDialog(ctx);
    case 'debt': return debtEditDialog(ctx);
    case 'income':
    case 'expense': return entryEditDialog(ctx);
    case 'fuel': return fuelEditDialog(ctx);
    case 'carEx': return carExpenseEditDialog(ctx);
    case 'trade': return tradeEditDialog(ctx);
    case 'recurring': return recurringEditDialog(ctx);
    default: return null;
  }
}

/**
 * Asked before the month's fixed costs go in a second time. The question is
 * the user's own wording; the note says what confirming actually does.
 */
export function recurringAgainDialog({ state, t, actions }) {
  const s = t.shell;
  const count = actions.recurringInMonth().length;
  if (!count) return null;

  const pill = (label, onClick, primary, key) => h('div', {
    key,
    ...tap(onClick),
    class: 'pt-button',
    style: css({
      flex: primary ? 1 : '0 0 110px', height: '40px', display: 'flex', alignItems: 'center',
      justifyContent: 'center', borderRadius: '10px', padding: '0 12px', textAlign: 'center',
      background: primary ? C.accent : 'transparent', color: primary ? C.bg : C.muted,
      border: primary ? 'none' : `1px solid ${C.stroke}`,
      fontFamily: MONO, fontSize: '11px', fontWeight: 700, letterSpacing: '.8px', cursor: 'pointer',
      opacity: primary && state.busy ? 0.5 : 1
    })
  }, label);

  return h('div', {
    key: 'recurring-again',
    class: 'pt-veil',
    onclick: event => {
      if (event.target === event.currentTarget) actions.closeRecurringAgain();
    },
    style: css({
      position: 'fixed', inset: 0, zIndex: 70, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '20px',
      background: 'rgba(4,8,12,.72)', overflowY: 'auto'
    })
  },
    h('div', {
      class: 'pt-rise',
      role: 'dialog',
      'aria-label': s.recurringAgainTitle,
      style: css({
        width: '100%', maxWidth: '380px', background: C.card,
        border: `1px solid ${C.stroke}`, borderRadius: '16px', padding: '20px',
        display: 'flex', flexDirection: 'column', gap: '12px',
        fontFamily: SANS, color: C.text
      })
    },
      h('div', { style: css({ fontSize: '15px', fontWeight: 700 }) }, s.recurringAgainTitle),
      h('div', { style: css({ fontSize: '13px', lineHeight: 1.5 }) }, s.recurringAgainQuestion),
      h('div', {
        style: css({ fontSize: '11.5px', color: C.faint, lineHeight: 1.5 })
      }, s.recurringAgainNote(count, state.month)),
      h('div', { style: css({ display: 'flex', gap: '8px', marginTop: '4px' }) },
        pill(s.recurringAgainYes, () => actions.postRecurring({ again: true }), true, 'yes'),
        pill(s.cancel, actions.closeRecurringAgain, false, 'no')
      )
    )
  );
}

export function holdingExitDialog({ state, t, fmt, actions }) {
  const row = state.data?.holdings?.find(h => h.id === state.holdingExit);
  if (!row) return null;

  const s = t.shell;
  const sold = Boolean(row.until);
  const monthName = fmt.monthName(state.month);

  const choice = (label, note, onClick, tone, key) => h('div', {
    key,
    style: css({ display: 'flex', flexDirection: 'column', gap: '5px' })
  },
    h('div', {
      ...tap(onClick),
      class: 'pt-button',
      style: css({
        height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '10px', background: tone === 'danger' ? 'transparent' : C.accent,
        color: tone === 'danger' ? C.danger : C.bg,
        border: tone === 'danger' ? `1px solid ${C.stroke}` : 'none',
        fontFamily: MONO, fontSize: '11px', fontWeight: 700, letterSpacing: '.8px',
        cursor: 'pointer', textAlign: 'center', padding: '0 12px'
      })
    }, label),
    h('div', {
      style: css({ fontSize: '11px', color: C.faint, lineHeight: 1.45 })
    }, note)
  );

  return h('div', {
    key: 'holding-exit',
    class: 'pt-veil',
    onclick: event => {
      if (event.target === event.currentTarget) actions.closeHoldingExit();
    },
    style: css({
      position: 'fixed', inset: 0, zIndex: 70, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '20px',
      background: 'rgba(4,8,12,.72)', overflowY: 'auto'
    })
  },
    h('div', {
      class: 'pt-rise', role: 'dialog', 'aria-modal': 'true', 'aria-label': s.exitTitle,
      style: css({
        width: '100%', maxWidth: '360px', background: C.card,
        border: `1px solid ${C.stroke}`, borderRadius: '16px', padding: '20px',
        display: 'flex', flexDirection: 'column', gap: '14px',
        fontFamily: SANS, color: C.text
      })
    },
      h('div', { style: css({ fontSize: '15px', fontWeight: 700 }) }, s.exitTitle),
      h('div', {
        style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.accent })
      }, `${row.ticker} · ${fmt.number(row.units)}`),

      sold
        ? choice(s.exitReopen, s.exitReopenNote(fmt.monthName(row.until)),
            () => actions.sellHolding(row.id, null), 'accent', 'reopen')
        : choice(s.exitSell(monthName.toUpperCase()), s.exitSellNote(monthName),
            () => actions.sellHolding(row.id, state.month), 'accent', 'sell'),

      choice(s.exitDelete, s.exitDeleteNote, () => actions.removeHolding(row.id), 'danger', 'delete'),

      h('div', {
        ...tap(actions.closeHoldingExit),
        class: 'pt-button',
        style: css({
          height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '10px', border: `1px solid ${C.stroke}`, color: C.muted,
          fontFamily: MONO, fontSize: '11px', fontWeight: 700, letterSpacing: '.8px',
          cursor: 'pointer'
        })
      }, s.cancel)
    )
  );
}


/**
 * Which way the page being rendered should arrive from.
 *
 * Reads the direction the store recorded when the tab was set, so both layouts
 * animate the same way from one decision. Anything that is not a step along the
 * nav strip -- a reload, the first paint, a jump from somewhere else -- rises
 * instead of sliding, because there is no direction to be faithful to.
 */
export const pageClass = state => (
  state.tabFrom === 1 ? 'pt-page-fwd'
    : state.tabFrom === -1 ? 'pt-page-back'
      : 'pt-page-in'
);
